import type { GetStaticPropsContext, InferGetStaticPropsType } from "next";
import type { NextSeoProps } from "next-seo";
import Link from "next/link";
import { useRouter } from "next/router";
import React, { ReactNode } from "react";

import { AppLayout } from "shared/Layout";
import { Markdown } from "shared/Markdown";
import { BestSellersCatalog } from "shared/catalog/BestSellersCatalog";
import { BestSellersContextProvider } from "shared/catalog/BestSellersCatalog/BestSellersContext";
import { getBestSellerEntries } from "shared/catalog/BestSellersCatalog/best-sellers-catalog.helper";
import { Button } from "ui/Button";
import { CACHE_ONE_DAY } from "utils/config.utils";
import { fetchPages } from "utils/fetch-page.utilts";
import { initializeApollo } from "utils/with-apollo.utils";
import { withApp } from "utils/with-app.utils";
import { fetchTranslations, useI18n } from "utils/with-i18n.utils";
import { withSeo } from "utils/with-seo.utils";

interface ContentPageData {
  id: string;
  slug: string;
  title: string;
  description: string;
  content: string;
}

const ContentPage = ({
  title,
  description,
  content,
  bestSellersEntries,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const i18n = useI18n();
  const { push } = useRouter();

  const handleGoToCatalog = () => push("/smartphone");

  if (!title || !description || !content) {
    return null;
  }

  return (
    <section className="flex flex-col items-center px-2 m-auto my-12 lg:px-0 lg:my-24">
      <h1 className="max-w-3xl text-4xl font-medium text-center lg:text-6xl">
        {title}
      </h1>

      <h2 className="mt-12 mb-4 max-w-3xl text-lg lg:mt-16 lg:mb-8">
        <strong>{description}</strong>
      </h2>

      <article className="max-w-3xl">
        <Markdown content={content} isSEOArticle />
      </article>

      <div className="flex flex-col items-center mx-auto mt-10 w-fit lg:mt-16">
        <h2 className="mt-4 text-5xl font-medium text-center">
          {i18n.t("errorPage.bestSellers")}
        </h2>

        <div className="flex flex-wrap gap-4 justify-center px-5 my-8 w-full max-w-6xl lg:my-14">
          <BestSellersContextProvider entries={bestSellersEntries}>
            <BestSellersCatalog />
          </BestSellersContextProvider>
        </div>

        <Button onClick={handleGoToCatalog}>
          <Link href="/smartphone">
            <a>{i18n.t("errorPage.otherDevices")}</a>
          </Link>
        </Button>
      </div>
    </section>
  );
};

export async function getStaticPaths() {
  const [pages] = await fetchPages<[ContentPageData[]]>("contents");

  return {
    paths: pages.map((page) => ({ params: { seoArticle: page.slug } })),
    fallback: false,
  };
}

export async function getStaticProps(ctx: GetStaticPropsContext) {
  const slug = ctx.params?.seoArticle as string;

  const apolloClient = initializeApollo();

  const translations = await fetchTranslations(["blog", "catalog", "marquee"]);

  const [pages] = await fetchPages<[ContentPageData[]]>("contents");

  const page = pages.find((pageData) => pageData.slug === slug);

  const bestSellersEntries = await getBestSellerEntries();

  const seo: NextSeoProps = {
    title: page?.title,
    description: page?.description,
  };

  return {
    props: {
      initialApolloState: apolloClient.cache.extract(),
      bestSellersEntries,
      translations,
      title: page?.title,
      description: page?.description,
      content: page?.content,
      seo,
    },
    revalidate: CACHE_ONE_DAY,
  };
}

ContentPage.getLayout = (page: ReactNode, pageProps: any) => (
  <AppLayout withBanner {...pageProps}>
    {page}
  </AppLayout>
);

export default withApp(ContentPage, [withSeo]);
